// Lead-form name + phone validation (client side).
//
// Stops junk like "1" / "2" reaching the lead endpoints and shows the visitor
// WHICH field is wrong, right under it. Works through the browser's constraint
// API: every lead-form handler (contact-form.js, lead-cta.js, evaluare.js)
// already gates on form.checkValidity(), so setCustomValidity() here is enough
// to block the submit - no handler needs to know this module exists.
//
// The submit listener sits on WINDOW in the capture phase, i.e. ahead of
// atpl-phone.js (document, capture), which rewrites the tel field to E.164,
// and ahead of the components' own bubble-phase handlers.
//
// Mirrors atpl_lead_validate_name() / atpl_lead_validate_phone() in
// inc/leads.php - the server re-checks, since a direct POST skips all of this.
import { t } from '../lib/i18n.js';

const LEAD_SCOPE = '[data-component="contact-form"], [data-component="lead-cta"], [data-component="evaluare-form"]';
const FIELD_SEL = 'input[name="name"], input[type="tel"][name="phone"]';

// Letters (any script - Romanian diacritics, Cyrillic), spaces and the
// punctuation real names use: hyphen, apostrophes, dot.
const NAME_CHARS = /^[\p{L}\p{M}][\p{L}\p{M}\s.'’`-]*$/u;

function isLeadForm(form) {
  return !!(form && form.nodeName === 'FORM' && (form.matches(LEAD_SCOPE) || form.closest(LEAD_SCOPE)));
}

function nameError(input) {
  const v = input.value.trim().replace(/\s+/g, ' ');
  if (!v) return input.required ? t('val_name_required', 'Introdu numele tău.') : '';
  if (/\d/.test(v)) return t('val_name_digits', 'Numele nu poate conține cifre.');
  if (!NAME_CHARS.test(v)) return t('val_name_chars', 'Numele poate conține doar litere.');
  const letters = v.replace(/[^\p{L}]/gu, '').toLowerCase();
  // "a", "aa", "ффф" - not a name.
  if (letters.length < 2 || /^(.)\1+$/u.test(letters)) return t('val_name_short', 'Introdu un nume valid (minim 2 litere).');
  return '';
}

// 11111111, 12345678, 87654321 - obvious keyboard filler.
function isJunkDigits(d) {
  if (/^(\d)\1+$/.test(d)) return true;
  return '01234567890'.includes(d) || '09876543210'.includes(d);
}

function phoneError(input) {
  const raw = input.value.trim();
  if (!raw) return input.required ? t('val_phone_required', 'Introdu numărul de telefon.') : '';
  const bad = t('val_phone_invalid', 'Număr de telefon invalid. Verifică cifrele.');
  if (/[^\d\s()+.\-]/.test(raw)) return bad;

  const iti = input.__atplIti; // set by atpl-phone.js (intl-tel-input)
  let dial = '';
  try { dial = (iti && iti.getSelectedCountryData().dialCode) || ''; } catch { dial = ''; }

  // National significant digits: drop an international prefix carrying the
  // selected country's code, then any trunk 0.
  let core = raw.replace(/\D/g, '');
  if (/^(\+|00)/.test(raw.replace(/\s/g, ''))) {
    core = core.replace(/^00/, '');
    if (dial && core.startsWith(dial)) core = core.slice(dial.length);
  }
  core = core.replace(/^0+/, '');
  if (core.length < 6 || isJunkDigits(core)) return bad;

  // utils.js loaded (lazily, on first focus) → real per-country number plan.
  if (iti && window.intlTelInput && window.intlTelInput.utils) {
    try { return iti.isValidNumberPrecise() ? '' : bad; } catch { /* fall through */ }
  }
  // No utils yet: length sanity only. Moldova is exactly 8 national digits.
  if (dial === '373') return core.length === 8 ? '' : bad;
  return core.length <= 14 ? '' : bad;
}

function errorFor(input) {
  return input.type === 'tel' ? phoneError(input) : nameError(input);
}

// The message element lives after the field - after the intl-tel-input wrapper
// for phones, and after the whole input+button row on the car-page CTA so the
// row's flex layout stays intact.
function messageEl(input, create) {
  if (input.__apErr) return input.__apErr;
  if (!create) return null;
  const anchor = input.closest('.ap-lead-cta__row') || input.closest('.iti') || input;
  const el = document.createElement('p');
  el.className = 'ap-input-error';
  el.id = `ap-err-${Math.random().toString(36).slice(2, 9)}`;
  el.setAttribute('aria-live', 'polite');
  el.hidden = true;
  anchor.insertAdjacentElement('afterend', el);
  input.__apErr = el;
  return el;
}

function render(input, msg) {
  const el = messageEl(input, !!msg);
  input.classList.toggle('is-error', !!msg);
  if (msg) {
    input.setAttribute('aria-invalid', 'true');
    input.setAttribute('aria-describedby', el.id);
  } else {
    input.removeAttribute('aria-invalid');
  }
  if (el) {
    el.textContent = msg;
    el.hidden = !msg;
  }
}

// Sync the constraint state; paint the message only when asked (blur/submit)
// or when one is already showing (so it updates/clears while typing).
function check(input, show) {
  const msg = errorFor(input);
  input.setCustomValidity(msg);
  if (show || input.classList.contains('is-error')) render(input, msg);
  return !msg;
}

function leadField(target) {
  return target && target.matches && target.matches(FIELD_SEL) && isLeadForm(target.form) ? target : null;
}

export function initLeadValidation() {
  document.addEventListener('input', (e) => {
    const input = leadField(e.target);
    if (input) check(input, false);
  });
  // Change covers autofill and the country dropdown (intl-tel-input fires
  // `countrychange`, which alters what a valid number is).
  document.addEventListener('change', (e) => {
    const input = leadField(e.target);
    if (input) check(input, false);
  });
  document.addEventListener('countrychange', (e) => {
    const input = leadField(e.target);
    if (input) check(input, false);
  });
  // Blur: flag a filled-in but wrong value. Empty fields are left alone until
  // submit, so tabbing through the form doesn't light everything up red.
  document.addEventListener('focusout', (e) => {
    const input = leadField(e.target);
    if (input) check(input, input.value.trim() !== '');
  });

  window.addEventListener('submit', (e) => {
    const form = e.target;
    if (!isLeadForm(form)) return;
    let first = null;
    form.querySelectorAll(FIELD_SEL).forEach((input) => {
      if (!check(input, true) && !first) first = input;
    });
    // The components see the custom validity via checkValidity() and abort on
    // their own; this also covers any lead form without such a handler.
    if (first) {
      e.preventDefault();
      first.focus();
    }
  }, true);

  // Forms that are reset after a successful send must not keep stale errors.
  document.addEventListener('reset', (e) => {
    if (!isLeadForm(e.target)) return;
    e.target.querySelectorAll(FIELD_SEL).forEach((input) => {
      input.setCustomValidity('');
      render(input, '');
    });
  }, true);
}
